// ────────────────────────────────────────────────────────────────────
// One row of the queue. The same eight fields whether the job is yours or
// somebody else's: stage chip · job id · client · title · evidence · NEXT
// ACTION · age · one verb.
//
// "Visible, not actionable" is a PERMISSION, not a shade of grey. A row the
// caller may not move renders no button, no menu item, nothing focusable that
// could be fired — the server already decided by withholding `action`, and
// this component's only job is not to reintroduce the decision. There is no
// `disabled` button here on purpose: a disabled control still says "this is
// yours, just not now", which is a different and untrue statement.
//
// Colour: content is --ink-3 or darker. --ink-4 (#a8a29e) measures 2.28–2.52:1
// on this paper and does not appear in this file at all — the job id and the
// client name are the two things you read to identify a row, and they were the
// exact fields found at 2.5:1 on the landing screen on 2026-08-31. Accent as
// text is --accent-deep; --accent is a fill.
// ────────────────────────────────────────────────────────────────────
import { Link } from "react-router-dom";
import type { QueueRow } from "./types";

const MICRO = {
  fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.08em",
  textTransform: "uppercase" as const, color: "var(--ink-3)",
};

function StageChip({ row }: { row: QueueRow }) {
  return (
    <span
      data-testid={`queue-row-stage-${row.id}`}
      style={{
        fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.06em",
        textTransform: "uppercase", whiteSpace: "nowrap",
        padding: "3px 7px", borderRadius: 3,
        border: "1px solid var(--hairline)",
        background: row.mine ? "var(--surface-warm-2)" : "var(--surface-2)",
        color: row.mine ? "var(--accent-deep)" : "var(--ink-2)",
      }}
    >
      {row.stage_label}
    </span>
  );
}

export function QueueRowCard(
  { row, onAdvance }: { row: QueueRow; onAdvance: (row: QueueRow) => void },
) {
  return (
    <div
      data-testid={`queue-row-${row.id}`}
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 15rem) 7rem 9rem",
        gap: 12, alignItems: "start",
        padding: "12px 14px",
        borderTop: "1px solid var(--line-2)",
        background: row.overdue ? "var(--surface-alert)" : "var(--paper-2)",
      }}
    >
      {/* The row opens the job. The verb sits OUTSIDE this anchor — a button
          nested in a link is invalid, and firing the verb must never be one
          mis-aimed pixel away from navigating instead. */}
      <Link
        to={`/jobs/${row.id}`}
        data-testid={`queue-row-open-${row.id}`}
        style={{ display: "block", textDecoration: "none", color: "inherit", minWidth: 0 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <StageChip row={row} />
          <span
            data-testid={`queue-row-ref-${row.id}`}
            style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--ink-2)" }}
          >
            {row.ref}
          </span>
          <span
            data-testid={`queue-row-client-${row.id}`}
            style={{
              fontSize: 11, padding: "2px 7px", borderRadius: 10,
              border: "1px solid var(--hairline)", background: "var(--surface-2)",
              color: "var(--ink-2)", whiteSpace: "nowrap",
            }}
          >
            {/* An em dash is the absence the wire reports, not a stand-in for
                a client nobody has named yet. */}
            {row.client ?? "—"}
          </span>
          {row.priority === "urgent" && (
            <span style={{ ...MICRO, color: "var(--err)" }}>urgent</span>
          )}
        </div>
        <div
          data-testid={`queue-row-title-${row.id}`}
          style={{ marginTop: 6, fontSize: 14, color: "var(--ink-1)", fontWeight: 500 }}
        >
          {row.title}
        </div>
        <div
          data-testid={`queue-row-evidence-${row.id}`}
          style={{ marginTop: 3, fontSize: 12, color: "var(--ink-3)" }}
        >
          {row.evidence}
        </div>
      </Link>

      <div style={{ minWidth: 0 }}>
        <div style={MICRO}>Next action</div>
        <div
          data-testid={`queue-row-next-${row.id}`}
          style={{ marginTop: 3, fontSize: 12, color: "var(--ink-2)" }}
        >
          {row.next_action}
        </div>
      </div>

      <div
        data-testid={`queue-row-age-${row.id}`}
        style={{
          fontFamily: "var(--mono)", fontSize: 11,
          color: row.overdue ? "var(--err)" : "var(--ink-3)",
        }}
      >
        {row.age_label}
      </div>

      <div>
        {row.action ? (
          <button
            type="button"
            data-testid={`queue-row-verb-${row.id}`}
            onClick={() => onAdvance(row)}
            title={`→ ${row.action.to_stage_label}`}
            style={{
              width: "100%", padding: "7px 10px", borderRadius: 3, cursor: "pointer",
              fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.06em",
              textTransform: "uppercase",
              border: "1px solid var(--accent-deep)",
              background: "var(--accent)", color: "var(--paper-2)",
            }}
          >
            {row.action.verb}
          </button>
        ) : (
          // No control at all — not a disabled one. The cell carries the
          // gate's own words when the server supplied a reason, and otherwise
          // stays empty: whose move it is has already been said, in the
          // server's sentence, in the NEXT ACTION column. Saying it a second
          // time in this browser's own words is how a screen ends up asserting
          // something the wire never told it.
          <div
            data-testid={`queue-row-noverb-${row.id}`}
            style={{ ...MICRO, textTransform: "none", letterSpacing: 0, fontSize: 11 }}
          >
            {row.action_blocked_reason ?? ""}
          </div>
        )}
      </div>
    </div>
  );
}
